'use client';
import React, { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/apiLogger';
import { Formik, Form } from 'formik';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { FloatLabel } from 'primereact/floatlabel';
import { Skeleton } from 'primereact/skeleton';
import * as Yup from 'yup';
import { classNames } from 'primereact/utils';
import { appConfig } from '@/_config';
import Link from 'next/link';
import { createSupportTicket } from '@/lib/support/supportTicketService';

// ─── Auth helper ──────────────────────────────────────────────────────────────
const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

const dataURLtoFile = (dataurl, filename) => {
    const [header, data] = dataurl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const bstr = atob(data);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
};

// ─── Validation schemas ────────────────────────────────────────────────────────
const guestSchema = Yup.object({
    name:      Yup.string().required('Name is required'),
    email:     Yup.string().matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email address').required('Email is required'),
    mobile:    Yup.string().matches(/^\+?[1-9]\d{9,9}$/, 'Invalid mobile number').required('Contact number is required'),
    queryType: Yup.string().required('Query type is required'),
    message:   Yup.string().required('Message is required'),
});

const loggedInSchema = Yup.object({
    queryType: Yup.string().required('Query type is required'),
    message:   Yup.string().required('Please describe your issue'),
});

// ─── Options ──────────────────────────────────────────────────────────────────
const roles = [
    { label: 'Support',                       value: 'Support' },
    { label: 'Sales Enquiry',                 value: 'Sales Enquiry' },
    { label: 'Help for automation setup',     value: 'Help for automation setup' },
    { label: 'Others',                        value: 'Others' },
];

// ─── Logged-in user identity card ─────────────────────────────────────────────
const LoggedInUserCard = ({ user }) => {
    const name   = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || 'You';
    const mobile = user.mobile || user.contact || '';
    const initial = (name[0] || user.email?.[0] || '?').toUpperCase();

    return (
        <div
            className="col-12"
            style={{
                border: '1.5px solid rgba(27,184,167,0.28)',
                borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(27,184,167,0.06) 0%, rgba(99,102,241,0.04) 100%)',
                padding: '14px 16px',
                marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 14,
            }}
        >
            {/* Avatar circle */}
            <div className ="bg-primary" style={{
                width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>{initial}</span>
            </div>

            {/* Fields */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 5px', fontWeight: 700, fontSize: '0.95rem' }} className='text-primary'>{name}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 18px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem' }} className="text-primary">
                        <i className="pi pi-envelope" style={{ fontSize: '0.62rem' }}  />
                        {user.email}
                    </span>
                    {mobile && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem' }} className="text-primary">
                            <i className="pi pi-phone" style={{ fontSize: '0.62rem' }}  />
                            {mobile}
                        </span>
                    )}
                </div>
            </div>

            {/* Badge */}
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                padding: '3px 10px', borderRadius: 20,
                background: 'rgba(27,184,167,0.1)', border: '1px solid rgba(27,184,167,0.3)',
                fontSize: '0.68rem', fontWeight: 700, color: '#1bb8a7',
            }}>
                <i className="pi pi-verified" style={{ fontSize: '0.58rem' }} />
                Logged in
            </span>
        </div>
    );
};

// ─── Support hint panel ────────────────────────────────────────────────────────
const SupportHintPanel = () => (
    <div className="col-12" style={{ animation: 'fadeIn 0.3s ease' }}>
        <div
            className="border-round-xl p-4"
            style={{
                background: 'linear-gradient(135deg, rgba(99,102,241,0.07) 0%, rgba(27,184,167,0.07) 100%)',
                border: '1.5px solid rgba(99,102,241,0.25)',
            }}
        >
            <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-info-circle" style={{ color: '#6366f1', fontSize: '1rem' }} />
                <span className="font-semibold text-sm" style={{ color: '#6366f1' }}>
                    Looking for product or order support?
                </span>
            </div>
            <div className="grid">
                <div className="col-12 md:col-6">
                    <Link href="/account/support" className="no-underline">
                        <div
                            className="border-round-lg p-3 h-full flex gap-3 align-items-start cursor-pointer transition-all transition-duration-200"
                            style={{ background: '#fff', border: '1.5px solid rgba(99,102,241,0.2)', boxShadow: '0 2px 10px rgba(99,102,241,0.08)' }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(99,102,241,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <div className="flex align-items-center justify-content-center border-circle flex-shrink-0" style={{ width: 40, height: 40, background: 'rgba(99,102,241,0.1)' }}>
                                <i className="pi pi-ticket" style={{ fontSize: '1.1rem', color: '#6366f1' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <p className="m-0 font-bold text-sm" style={{ color: '#1e1b4b' }}>Raise a Support Ticket</p>
                                <p className="m-0 mt-1 text-xs" style={{ color: '#475569' }}>Get dedicated help — track your issue from open to resolved.</p>
                                <div className="flex align-items-center gap-1 mt-2" style={{ color: '#6366f1', fontSize: '0.75rem', fontWeight: 600 }}>
                                    <span>Go to My Tickets</span>
                                    <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>
                <div className="col-12 md:col-6">
                    <Link href="/support" className="no-underline">
                        <div
                            className="border-round-lg p-3 h-full flex gap-3 align-items-start cursor-pointer transition-all transition-duration-200"
                            style={{ background: '#fff', border: '1.5px solid rgba(27,184,167,0.2)', boxShadow: '0 2px 10px rgba(27,184,167,0.08)' }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(27,184,167,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(27,184,167,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <div className="flex align-items-center justify-content-center border-circle flex-shrink-0" style={{ width: 40, height: 40, background: 'rgba(27,184,167,0.1)' }}>
                                <i className="pi pi-headphones" style={{ fontSize: '1.1rem', color: '#1bb8a7' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <p className="m-0 font-bold text-sm" style={{ color: '#134e4a' }}>Support Centre</p>
                                <p className="m-0 mt-1 text-xs" style={{ color: '#475569' }}>Browse FAQs, warranty info, and reach our team directly.</p>
                                <div className="flex align-items-center gap-1 mt-2" style={{ color: '#1bb8a7', fontSize: '0.75rem', fontWeight: 600 }}>
                                    <span>Visit Support Page</span>
                                    <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
            <p className="m-0 mt-3 text-xs text-400 flex align-items-center gap-1">
                <i className="pi pi-info-circle" style={{ fontSize: '0.7rem' }} />
                You can still use this form — we just wanted to make sure you knew about faster options.
            </p>
        </div>
        <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
);

// ─── Ticket number copy ────────────────────────────────────────────────────────
const TicketNumberCopy = ({ ticketNumber }) => {
    const [copied, setCopied] = useState(false);
    const doCopy = () => {
        navigator.clipboard.writeText(ticketNumber).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="flex align-items-center gap-2 border-round px-3 py-2 mb-3 mt-1" style={{ background: 'rgba(27,184,167,0.08)', border: '1px solid rgba(27,184,167,0.25)' }}>
            <span className="text-xs" style={{ color: '#64748b' }}>Ticket No:</span>
            <span className="font-bold text-sm" style={{ color: '#1bb8a7' }}>{ticketNumber}</span>
            <button
                type="button"
                onClick={doCopy}
                title="Copy ticket number"
                style={{
                    marginLeft: 4, background: copied ? 'rgba(27,184,167,0.18)' : 'transparent',
                    border: '1px solid rgba(27,184,167,0.3)', borderRadius: 6,
                    padding: '2px 6px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4,
                    color: copied ? '#1bb8a7' : '#64748b', fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.15s',
                }}
            >
                <i className={copied ? 'pi pi-check' : 'pi pi-copy'} style={{ fontSize: '0.7rem' }} />
                {copied ? 'Copied!' : 'Copy'}
            </button>
        </div>
    );
};

// ─── Success screen ────────────────────────────────────────────────────────────
const SuccessScreen = ({ data }) => (
    <div className="flex flex-column align-items-center justify-content-center text-center py-6 px-4" style={{ minHeight: '18rem' }}>
        <div
            className="flex align-items-center justify-content-center border-circle mb-4 bg-primary"
            style={{ width: 72, height: 72, boxShadow: '0 8px 24px rgba(27,184,167,0.35)' }}
        >
            <i className="pi pi-check" style={{ fontSize: '1.8rem', color: '#fff' }} />
        </div>
        <h2 className="m-0 mb-2" style={{ fontSize: '1.4rem', color: '#0f172a' }}>Thank you, {data?.name}!</h2>
        {data?.ticketNumber ? (
            <>
                <p className="m-0 text-900 mb-1" style={{ maxWidth: '24rem' }}>Your support ticket has been raised successfully.</p>
                <TicketNumberCopy ticketNumber={data.ticketNumber} />
            </>
        ) : (
            <p className="m-0 text-500 mb-1" style={{ maxWidth: '24rem' }}>Your message has been submitted. We&apos;ll get back to you shortly.</p>
        )}
        {data?.mobile && (
            <p className="m-0 text-sm text-500 mb-4">
                We&apos;ll contact you on <strong>{data.mobile}</strong>
            </p>
        )}
        <p className="m-0 text-xs text-100 mb-4">Regards, Team {appConfig.appShortName}</p>
        {data?.ticketNumber && (
            <Link href="/account/support" className="no-underline">
                <div
                    className="flex align-items-center gap-2 border-round-lg px-4 py-2 cursor-pointer"
                    style={{
                        background: 'linear-gradient(135deg, rgba(27,184,167,0.1), rgba(99,102,241,0.1))',
                        border: '1.5px solid rgba(27,184,167,0.3)',
                        color: '#1bb8a7', fontWeight: 700, fontSize: '0.88rem', transition: 'all 0.15s',
                    }}
                >
                    <i className="pi pi-list" style={{ fontSize: '0.8rem' }} />
                    View My Tickets
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
                </div>
            </Link>
        )}
    </div>
);

// ─── File icon helper ─────────────────────────────────────────────────────────
const fileIcon = (name = '', type = '') => {
    if (type.startsWith('image/'))                         return { icon: 'pi pi-image',    color: '#38bdf8' };
    if (name.endsWith('.pdf') || type === 'application/pdf') return { icon: 'pi pi-file-pdf', color: '#f87171' };
    if (name.match(/\.(doc|docx)$/i))                     return { icon: 'pi pi-file-word', color: '#60a5fa' };
    if (name.match(/\.(xls|xlsx|csv)$/i))                 return { icon: 'pi pi-file-excel',color: '#4ade80' };
    if (name.match(/\.(zip|rar|7z|tar|gz)$/i))            return { icon: 'pi pi-box',       color: '#fb923c' };
    if (name.match(/\.(mp4|mov|avi|mkv|webm)$/i))         return { icon: 'pi pi-video',     color: '#a78bfa' };
    if (name.match(/\.(mp3|wav|aac|ogg)$/i))              return { icon: 'pi pi-volume-up', color: '#f472b6' };
    return { icon: 'pi pi-file', color: '#94a3b8' };
};

const FILE_TYPES = [
    { label: 'Image', icon: 'pi pi-image',     color: '#38bdf8' },
    { label: 'PDF',   icon: 'pi pi-file-pdf',  color: '#f87171' },
    { label: 'DOC',   icon: 'pi pi-file-word', color: '#60a5fa' },
    { label: 'ZIP',   icon: 'pi pi-box',       color: '#fb923c' },
    { label: 'Video', icon: 'pi pi-video',     color: '#a78bfa' },
];

// ─── Universal attachment section (all file types) ────────────────────────────
const AttachSection = ({ attachments, setFieldValue }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

        {/* ── Highlighted header bar ── */}
        <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem',
            background: 'linear-gradient(135deg, rgba(56,189,248,0.08) 0%, rgba(99,102,241,0.06) 100%)',
            border: '1px solid rgba(56,189,248,0.25)',
            borderRadius: '0.75rem',
            padding: '0.65rem 0.9rem',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'rgba(56,189,248,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <i className="pi pi-paperclip" style={{ fontSize: '0.72rem', color: '#38bdf8' }} />
                </div>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0ea5e9' }}>Attachments</span>
                <span style={{
                    fontSize: '0.65rem', fontWeight: 600,
                    background: 'rgba(56,189,248,0.12)', color: '#38bdf8',
                    border: '1px solid rgba(56,189,248,0.25)',
                    borderRadius: 999, padding: '1px 7px',
                }}>Optional</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginLeft: 'auto' }}>
                {FILE_TYPES.map(({ label, icon, color }) => (
                    <span key={label} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: '0.65rem', fontWeight: 600,
                        color, background: `${color}14`,
                        border: `1px solid ${color}33`,
                        borderRadius: 999, padding: '2px 8px',
                    }}>
                        <i className={icon} style={{ fontSize: '0.58rem' }} />
                        {label}
                    </span>
                ))}
            </div>

            <div style={{ width: '100%', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="pi pi-copy" style={{ fontSize: '0.6rem' }} /> Up to 5 files
                </span>
                <span style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="pi pi-database" style={{ fontSize: '0.6rem' }} /> 10 MB each
                </span>
            </div>
        </div>

        {/* ── Preview grid ── */}
        {attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '0.25rem 0' }}>
                {attachments.map((f, i) => {
                    const { icon, color } = fileIcon(f.name, f.type);
                    return (
                        <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                            {f.isImage ? (
                                <img
                                    src={f.data}
                                    alt={f.name}
                                    style={{ width: 68, height: 68, objectFit: 'cover', borderRadius: 8, border: `1px solid ${color}55`, display: 'block' }}
                                />
                            ) : (
                                <div style={{
                                    width: 68, height: 68, borderRadius: 8,
                                    border: `1px solid ${color}44`, background: `${color}11`,
                                    padding: 4, overflow: 'hidden',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                                }}>
                                    <i className={icon} style={{ fontSize: '1.4rem', color }} />
                                    <span style={{ fontSize: '0.52rem', color: '#94a3b8', wordBreak: 'break-all', textAlign: 'center', maxWidth: 60, lineHeight: 1.2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                        {f.name}
                                    </span>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => setFieldValue('attachments', attachments.filter((_, j) => j !== i))}
                                style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >✕</button>
                        </div>
                    );
                })}
            </div>
        )}

        {/* ── Upload trigger ── */}
        {attachments.length < 5 ? (
            <label
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 6, cursor: 'pointer', borderRadius: '0.75rem', padding: '1.25rem 1rem',
                    border: '1.5px dashed rgba(56,189,248,0.3)',
                    background: 'rgba(56,189,248,0.03)',
                    transition: 'background 0.2s, border-color 0.2s',
                    textAlign: 'center',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(56,189,248,0.07)'; e.currentTarget.style.borderColor = 'rgba(56,189,248,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(56,189,248,0.03)'; e.currentTarget.style.borderColor = 'rgba(56,189,248,0.3)'; }}
            >
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(56,189,248,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-cloud-upload" style={{ fontSize: '1.2rem', color: '#38bdf8' }} />
                </div>
                <span style={{ color: '#0ea5e9', fontSize: '0.83rem', fontWeight: 700 }}>
                    Click to upload files
                </span>
                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                    {attachments.length > 0 ? `${attachments.length}/5 files added` : 'Any format supported'}
                </span>
                <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                        const files = Array.from(e.target.files || []);
                        const remaining = 5 - attachments.length;
                        files.slice(0, remaining).forEach(file => {
                            if (file.size > 10 * 1024 * 1024) return;
                            const isImage = file.type.startsWith('image/');
                            const reader = new FileReader();
                            reader.onload = ev => {
                                setFieldValue('attachments', [
                                    ...attachments,
                                    { name: file.name, type: file.type, data: ev.target.result, isImage },
                                ]);
                            };
                            reader.readAsDataURL(file);
                        });
                        e.target.value = '';
                    }}
                />
            </label>
        ) : (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '0.75rem', borderRadius: '0.75rem',
                background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.2)',
            }}>
                <i className="pi pi-check-circle" style={{ fontSize: '0.8rem', color: '#fb923c' }} />
                <span style={{ fontSize: '0.75rem', color: '#fb923c', fontWeight: 600 }}>Maximum 5 files reached</span>
            </div>
        )}
    </div>
);

// ─── Main ContactForm ──────────────────────────────────────────────────────────
export const ContactForm = ({ title, initialQueryType, initialMessage }) => {
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [authUser,      setAuthUser]      = useState(null);
    const [hydrated,      setHydrated]      = useState(false);

    useEffect(() => {
        setAuthUser(getAuthUser());
        setHydrated(true);
    }, []);

    const userName   = authUser ? (authUser.name || [authUser.firstName, authUser.lastName].filter(Boolean).join(' ') || '') : '';
    const userEmail  = authUser?.email  || '';
    const userMobile = authUser?.mobile || authUser?.contact || '';

    const initialValues = {
        name:        userName,
        email:       userEmail,
        mobile:      userMobile,
        queryType:   initialQueryType || (title ? 'Sales Enquiry' : ''),
        message:     initialMessage   || (title
            ? 'I am looking to set up a smart home automation system for my home and would like to inquire about your services and available solutions.'
            : ''),
        attachments: [],
    };

    const doSubmit = async (values, { setSubmitting }) => {
        const user        = authUser;
        const contactInfo = userMobile || userEmail;

        if (user) {
            try {
                const allFiles = values.attachments
                    .map(a => dataURLtoFile(a.data, a.name || (a.isImage ? 'image.jpg' : 'file')));

                const result = await createSupportTicket({
                    name:        userName || 'Customer',
                    email:       userEmail,
                    mobile:      userMobile || userEmail,
                    queryType:   values.queryType,
                    message:     values.message,
                    attachments: allFiles,
                });

                setSubmitting(false);
                setFormSubmitted({
                    name:         userName || 'Customer',
                    mobile:       contactInfo,
                    ticketNumber: result.data?.ticketNumber,
                });
                return;
            } catch {
                // fall through to guest path
            }
        }

        // Guest → raise ticket via /api/get-in-touch
        const res = await apiFetch('/api/get-in-touch', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
                ...values,
                mobile: values.mobile || values.email,
            }),
        });
        let json = {};
        try { json = await res.json(); } catch { /* ignore */ }

        setSubmitting(false);
        setFormSubmitted({
            name:         values.name,
            mobile:       values.mobile || values.email,
            ticketNumber: json?.data?.ticketNumber || json?.ticketNumber,
        });
    };

    if (formSubmitted) {
        return (
            <div className="container" style={{ maxWidth: '52rem', margin: '0 auto', padding: '2rem 1rem' }}>
                <div className="border-round-2xl overflow-hidden" style={{ background: '#fff', boxShadow: '0 4px 32px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
                    <SuccessScreen data={formSubmitted} />
                </div>
            </div>
        );
    }

    // Show skeleton until cookie is read on client
    if (!hydrated) {
        return (
            <div className="grid" style={{ rowGap: '0.75rem', padding: '0 0.5rem' }}>
                <div className="col-12"><Skeleton height="76px" borderRadius="14px" /></div>
                <div className="col-12 md:col-6"><Skeleton height="43px" /></div>
                <div className="col-12"><Skeleton height="180px" borderRadius="8px" /></div>
            </div>
        );
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={authUser ? loggedInSchema : guestSchema}
            enableReinitialize
            onSubmit={doSubmit}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
                const isSupport = values.queryType === 'Support';

                const doAddTemplate = () =>
                    setFieldValue('message',
                        `I am looking to set up a smart home automation system for my 3BHK apartment and would like to inquire about your services and available solutions.\n` +
                        `Specifically, I am interested in:\n\nSmart switches for lights and fans\nVoice control integration (e.g., Alexa, Google Assistant, Siri)\nMobile app control\n\n` +
                        `Could you please share details on your products, pricing, installation process, and any available packages or demos?\n\nLooking forward to your response.`
                    );

                return (
                    <Form>
                        <div className="grid" style={{ rowGap: '0.25rem' }}>

                            {/* ── Logged-in: identity card ── */}
                            {authUser && (
                                <div className="col-12 px-2 mb-1">
                                    <LoggedInUserCard user={authUser} />
                                </div>
                            )}

                            {/* ── Query type (always shown) ── */}
                            <div className="col-12 md:col-6">
                                <div className="field px-2">
                                    {isSubmitting
                                        ? <Skeleton height="43px" className="w-full" />
                                        : <>
                                            <FloatLabel className="w-full">
                                                <Dropdown
                                                    id="queryType"
                                                    name="queryType"
                                                    value={values.queryType}
                                                    options={roles}
                                                    onChange={e => setFieldValue('queryType', e.value)}
                                                    onBlur={handleBlur}
                                                    className={classNames({ 'p-invalid': touched.queryType && errors.queryType }, 'w-full')}
                                                />
                                                <label htmlFor="queryType">What can we help you with?</label>
                                            </FloatLabel>
                                            <div className="help-height">
                                                {touched.queryType && errors.queryType && (
                                                    <small className="p-error pl-1">{errors.queryType}</small>
                                                )}
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>

                            {/* ── Guest only: Full name ── */}
                            {!authUser && (
                                <div className="col-12 md:col-6">
                                    <div className="field px-2">
                                        {isSubmitting
                                            ? <Skeleton height="43px" className="w-full" />
                                            : <>
                                                <FloatLabel className="w-full">
                                                    <InputText
                                                        id="name"
                                                        value={values.name}
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        className={classNames({ 'p-invalid': touched.name && errors.name }, 'w-full')}
                                                    />
                                                    <label htmlFor="name">Full Name</label>
                                                </FloatLabel>
                                                <div className="help-height">
                                                    {touched.name && errors.name && (
                                                        <small className="p-error pl-1">{errors.name}</small>
                                                    )}
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>
                            )}

                            {/* ── Support hint panel ── */}
                            {isSupport && <SupportHintPanel />}

                            {/* ── Guest only: Mobile ── */}
                            {!authUser && (
                                <div className="col-12 md:col-6">
                                    <div className="field px-2">
                                        {isSubmitting
                                            ? <Skeleton height="43px" className="w-full" />
                                            : <>
                                                <FloatLabel className="w-full">
                                                    <InputText
                                                        id="mobile"
                                                        value={values.mobile}
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        type="tel"
                                                        inputMode="tel"
                                                        className={classNames({ 'p-invalid': touched.mobile && errors.mobile }, 'w-full')}
                                                    />
                                                    <label htmlFor="mobile">Contact Number</label>
                                                </FloatLabel>
                                                <div className="help-height">
                                                    {touched.mobile && errors.mobile
                                                        ? <small className="p-error pl-1">{errors.mobile}</small>
                                                        : <small className="flex align-items-center gap-1 mt-1 text-xs text-500">
                                                            <i className="pi pi-info-circle text-xs" />
                                                            I agree to be contacted via WhatsApp
                                                        </small>
                                                    }
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>
                            )}

                            {/* ── Guest only: Email ── */}
                            {!authUser && (
                                <div className="col-12 md:col-6">
                                    <div className="field px-2">
                                        {isSubmitting
                                            ? <Skeleton height="43px" className="w-full" />
                                            : <>
                                                <FloatLabel className="w-full">
                                                    <InputText
                                                        id="email"
                                                        type="email"
                                                        value={values.email}
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        className={classNames({ 'p-invalid': touched.email && errors.email }, 'w-full')}
                                                    />
                                                    <label htmlFor="email">Email Address</label>
                                                </FloatLabel>
                                                <div className="help-height">
                                                    {touched.email && errors.email && (
                                                        <small className="p-error pl-1">{errors.email}</small>
                                                    )}
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>
                            )}

                            {/* ── Message ── */}
                            <div className="col-12">
                                <div className="field px-2">
                                    {isSubmitting
                                        ? <Skeleton height="150px" className="w-full" />
                                        : authUser ? (
                                            /* Logged-in: plain textarea, no template button clutter */
                                            <>
                                                <label className="text-xs font-medium mb-2 block" style={{ color: '#94a3b8' }}>
                                                    Describe your issue <span style={{ color: '#f87171' }}>*</span>
                                                </label>
                                                <textarea
                                                    id="message"
                                                    name="message"
                                                    value={values.message}
                                                    onChange={handleChange}
                                                    placeholder="Tell us what's happening — the more detail, the faster we can help…"
                                                    rows={5}
                                                    style={{
                                                        width: '100%', boxSizing: 'border-box',
                                                        border: `1.5px solid ${touched.message && errors.message ? '#f87171' : '#e2e8f0'}`,
                                                        borderRadius: 10, padding: '12px 14px',
                                                        fontSize: '0.88rem', lineHeight: 1.65,
                                                        color: '#1e293b', background: '#f8faff',
                                                        resize: 'vertical', outline: 'none',
                                                        fontFamily: 'inherit',
                                                        transition: 'border-color 0.15s',
                                                    }}
                                                    onFocus={e => { e.target.style.borderColor = '#38bdf8'; }}
                                                    onBlur={e => { handleBlur(e); e.target.style.borderColor = touched.message && errors.message ? '#f87171' : '#e2e8f0'; }}
                                                />
                                                <div className="help-height">
                                                    {touched.message && errors.message && (
                                                        <small className="p-error pl-1">{errors.message}</small>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            /* Guest: rich Editor with Template / Clear helpers */
                                            <>
                                                <div className="flex align-items-center justify-content-between mb-2">
                                                    <label className="text-xs font-medium" style={{ color: '#94a3b8' }}>Your message or query</label>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button" label="Template" icon="pi pi-book" size="small"
                                                            onClick={doAddTemplate}
                                                            className="p-button-outlined p-button-secondary"
                                                            style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }}
                                                        />
                                                        <Button
                                                            type="button" label="Clear" icon="pi pi-times" size="small"
                                                            onClick={() => setFieldValue('message', '')}
                                                            className="p-button-outlined p-button-danger"
                                                            style={{ fontSize: '0.72rem', padding: '0.3rem 0.7rem' }}
                                                        />
                                                    </div>
                                                </div>
                                                <textarea
                                                    id="message"
                                                    name="message"
                                                    value={values.message}
                                                    onChange={handleChange}
                                                    placeholder="Tell us what's happening — the more detail, the faster we can help…"
                                                    rows={6}
                                                    style={{
                                                        width: '100%', boxSizing: 'border-box',
                                                        border: `1.5px solid ${touched.message && errors.message ? '#f87171' : '#e2e8f0'}`,
                                                        borderRadius: 10, padding: '12px 14px',
                                                        fontSize: '0.88rem', lineHeight: 1.65,
                                                        color: '#1e293b', background: '#f8faff',
                                                        resize: 'vertical', outline: 'none',
                                                        fontFamily: 'inherit',
                                                        transition: 'border-color 0.15s',
                                                    }}
                                                    onFocus={e => { e.target.style.borderColor = '#38bdf8'; }}
                                                    onBlur={e => { handleBlur(e); e.target.style.borderColor = touched.message && errors.message ? '#f87171' : '#e2e8f0'; }}
                                                />
                                                <div className="help-height">
                                                    {touched.message && errors.message && (
                                                        <small className="p-error pl-1">{errors.message}</small>
                                                    )}
                                                </div>
                                            </>
                                        )
                                    }
                                </div>
                            </div>

                            {/* ── Attachments ── */}
                            <div className="col-12">
                                <div className="field px-2">
                                    {isSubmitting
                                        ? <Skeleton height="60px" className="w-full" />
                                        : <AttachSection attachments={values.attachments} setFieldValue={setFieldValue} />
                                    }
                                </div>
                            </div>

                        </div>

                        {/* ── Submit ── */}
                        <div className="flex justify-content-center mt-3 pb-2">
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                label={isSubmitting ? 'Sending…' : 'Send Message'}
                                icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                                style={{
                                    background: 'var(--primary-color)',
                                    border: 'none', borderRadius: '0.65rem',
                                    fontWeight: 700, fontSize: '0.9rem',
                                    padding: '0.65rem 2rem',
                                    boxShadow: '0 4px 16px rgba(11,49,99,0.25)',
                                    minWidth: '12rem',
                                }}
                            />
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
